// ============================================================
// 声明式组件（optionTemplate）渲染与校验 —— 单一事实源
// 2026-10-15 组件编辑器改造 P0：
//  - 运行时（dtwin-web chartOptionFactory）/ 服务端 SSR 预览 / 编辑器实时预览共用本管线
//  - 权限：服务端 upload 的声明式契约校验与前端预检共用 validateDeclarativeContract（消息逐字一致）
// 渲染管线（权威顺序）：clone(option) → fieldPaths（结构化属性） → optionAdvanced（专家覆盖，
//   数据注入前、剥离 series[].data） → 数据注入（永远最后）
// ============================================================

/** 声明式组件模板（optionTemplate；fieldPaths 为 2026-10 新增可选契约） */
export interface DeclarativeTemplate {
  option?: Record<string, unknown> | null;
  /** 数据映射：'label/value'（单系列）或 'A,B,C'（多系列 key，≤12） */
  dataMapping?: string | null;
  /** schema.field → option 路径（支持 '*' 通配数组全元素；禁止含 data 段；≤50 条） */
  fieldPaths?: Record<string, string> | null;
}

export interface DeclarativeBuildOptions {
  /** 组件属性值（扁平 config；含控件特有字段与 optionAdvanced） */
  config?: Record<string, unknown> | null;
  /** 数据行（运行时来自数据绑定；预览用 DEFAULT_PREVIEW_ROWS） */
  rows?: Array<Record<string, unknown>> | null;
}

export const DECLARATIVE_DATA_MAPPING_DEFAULT = 'label/value';

/** P2（2026-10-16）：受限映射 DSL 前缀（`dsl:x=<字段>,y=<字段>[,series=a|b|c]`；旧值不受影响） */
export const DATA_MAPPING_DSL_PREFIX = 'dsl:';
const DATA_FIELD_RE = /^[a-zA-Z_][\w.-]*$/;

export interface ParsedDataMapping {
  kind: 'label-value' | 'keys' | 'dsl';
  /** kind=keys 时的多系列字段名 */
  keys: string[];
  /** kind=dsl 时解析出的变量（x=类目标签字段；y=单系列数值字段；series=多系列字段） */
  dsl?: { x?: string; y?: string; series?: string[] };
}

/** 解析 dataMapping（label/value | 逗号分隔 keys | dsl:...）；畸形 dsl 尽量解析（校验由 validateDataMappingValue 负责） */
export function parseDataMapping(mapping: unknown): ParsedDataMapping {
  const raw = String(mapping ?? DECLARATIVE_DATA_MAPPING_DEFAULT).trim();
  if (raw.startsWith(DATA_MAPPING_DSL_PREFIX)) {
    const dsl: NonNullable<ParsedDataMapping['dsl']> = {};
    for (const part of raw.slice(DATA_MAPPING_DSL_PREFIX.length).split(',').map((s) => s.trim()).filter(Boolean)) {
      const idx = part.indexOf('=');
      if (idx <= 0) continue;
      const key = part.slice(0, idx).trim();
      const value = part.slice(idx + 1).trim();
      if (key === 'x' || key === 'y') {
        if (value) dsl[key] = value;
      } else if (key === 'series') {
        const list = value.split('|').map((s) => s.trim()).filter(Boolean);
        if (list.length > 0) dsl.series = list;
      }
    }
    return { kind: 'dsl', keys: [], dsl };
  }
  if (raw === DECLARATIVE_DATA_MAPPING_DEFAULT) return { kind: 'label-value', keys: [] };
  return { kind: 'keys', keys: raw.split(',').map((s) => s.trim()).filter(Boolean) };
}

/** dataMapping 校验（返回错误消息或 null；服务端权威 + 编辑器预检共用） */
export function validateDataMappingValue(mapping: unknown): string | null {
  if (mapping === undefined || mapping === null || String(mapping).trim() === '') return null;
  const raw = String(mapping).trim();
  if (raw === DECLARATIVE_DATA_MAPPING_DEFAULT) return null;
  const GENERIC = 'dataMapping 非法（应为 label/value 或逗号分隔字段名，≤12 项）';
  if (raw.startsWith(DATA_MAPPING_DSL_PREFIX)) {
    const body = raw.slice(DATA_MAPPING_DSL_PREFIX.length);
    if (!body.trim()) return 'dataMapping dsl 不能为空（应为 dsl:x=字段,y=字段[,series=a|b]）';
    for (const part of body.split(',').map((s) => s.trim()).filter(Boolean)) {
      const idx = part.indexOf('=');
      if (idx <= 0) return `dataMapping dsl 语法非法（应为 key=字段）：${part}`;
      const key = part.slice(0, idx).trim();
      const value = part.slice(idx + 1).trim();
      if (key !== 'x' && key !== 'y' && key !== 'series') return `dataMapping dsl 变量 key 仅支持 x/y/series：${key}`;
      if (key === 'series') {
        const list = value.split('|').map((s) => s.trim()).filter(Boolean);
        if (list.length === 0 || list.length > 12) return 'dataMapping dsl series 须 1~12 项（用 | 分隔）';
        if (list.some((f) => !DATA_FIELD_RE.test(f))) return `dataMapping dsl 字段名不合法：${value}`;
      } else if (!value || !DATA_FIELD_RE.test(value)) {
        return `dataMapping dsl 字段名不合法：${value}`;
      }
    }
    return null;
  }
  const keys = raw.split(',').map((s) => s.trim());
  if (keys.length > 12 || keys.some((k) => !DATA_FIELD_RE.test(k))) return GENERIC;
  return null;
}

/** 高级选项（JSON）限制：≤20KB / 深合并深度 ≤10 */
export const OPTION_ADVANCED_LIMITS = { maxBytes: 20 * 1024, maxDepth: 10 } as const;

/** 预览注入默认样例行（服务端 SSR 历史演示数据：一月…六月 / 120,200,150,80,70,110，逐字保留） */
export const DEFAULT_PREVIEW_ROWS: Array<{ label: string; value: number }> = [
  { label: '一月', value: 120 },
  { label: '二月', value: 200 },
  { label: '三月', value: 150 },
  { label: '四月', value: 80 },
  { label: '五月', value: 70 },
  { label: '六月', value: 110 },
];

/** 多系列预览合成数据基值（历史行为：base[ri] + ((i*17 + ri*29) % 50)） */
const PREVIEW_MULTI_BASE = [40, 90, 65, 120, 80, 140];

// ------------------------------------------------------------
// 基础工具
// ------------------------------------------------------------

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** 剥离 option.series[].data（数据永远由 dataMapping 注入，禁止经高级项覆盖） */
export function stripSeriesData(option: Record<string, unknown> | null | undefined): void {
  const series = option?.series;
  if (!Array.isArray(series)) return;
  for (const s of series) {
    if (isPlainObject(s) && 'data' in s) delete (s as Record<string, unknown>).data;
  }
}

/**
 * 解析「高级选项（JSON）」：非法/空/超限返回 null（调用方自行 warn）。
 * 成功 = 对象且已剥离 series[].data。
 */
export function parseOptionAdvanced(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  if (raw.length > OPTION_ADVANCED_LIMITS.maxBytes) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isPlainObject(parsed)) return null;
    stripSeriesData(parsed);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 深合并（advanced → 模板 option）：对象递归、数组整体替换（series 按索引合并对象、超出追加）；
 * 深度护栏 OPTION_ADVANCED_LIMITS.maxDepth。
 */
export function mergeOptionInto(target: Record<string, unknown>, source: Record<string, unknown>, depth = 0): void {
  if (depth > OPTION_ADVANCED_LIMITS.maxDepth) return;
  for (const key of Object.keys(source)) {
    const sv = source[key];
    const tv = target[key];
    if (key === 'series' && Array.isArray(sv) && Array.isArray(tv)) {
      sv.forEach((item, i) => {
        if (isPlainObject(item) && isPlainObject(tv[i])) {
          mergeOptionInto(tv[i] as Record<string, unknown>, item, depth + 1);
        } else if (i < tv.length) {
          tv[i] = item;
        } else {
          tv.push(item);
        }
      });
      continue;
    }
    if (isPlainObject(sv) && isPlainObject(tv)) {
      mergeOptionInto(tv as Record<string, unknown>, sv, depth + 1);
    } else {
      target[key] = sv;
    }
  }
}

/**
 * 应用 fieldPaths：config[field] !== undefined 时写入 option 路径。
 * - '*' 展开数组全元素；中间对象缺失时自动创建（下一段为 '*' 且不存在数组则跳过，不臆造数组）
 * - 路径含 data 段一律跳过（防御；契约校验已禁止）
 */
export function applyFieldPaths(
  option: Record<string, unknown>,
  fieldPaths: Record<string, string> | null | undefined,
  config: Record<string, unknown> | null | undefined,
): void {
  if (!fieldPaths || !config) return;
  for (const [field, rawPath] of Object.entries(fieldPaths)) {
    const value = config[field];
    if (value === undefined) continue;
    const path = String(rawPath || '').trim();
    const segs = path.split('.').filter(Boolean);
    if (segs.length === 0 || segs.some((s) => s === 'data')) continue;
    setByPath(option, segs, value);
  }
}

function setByPath(target: Record<string, unknown>, segs: string[], value: unknown): void {
  const [head, ...rest] = segs;
  if (rest.length === 0) {
    if (head === '*') return; // 末段通配无赋值语义
    target[head] = value;
    return;
  }
  if (head === '*') {
    if (Array.isArray(target)) {
      for (const item of target) {
        if (isPlainObject(item)) setByPath(item, rest, value);
      }
    }
    return;
  }
  let next = target[head] as unknown;
  if (next === undefined || next === null || typeof next !== 'object') {
    // 不臆造数组（如 series 不存在时的 series.*.x）；标量键缺失则创建中间对象
    if (rest[0] === '*') return;
    next = {};
    target[head] = next;
  }
  setByPath(next as Record<string, unknown>, rest, value);
}

/** schema 默认值 → 扁平 config（SSR 预览与运行时默认值一致）
 *  2026-10-17：json 控件的默认值存字符串（ConfigPanel 运行时提交对象）→ 预览/SSR 组装时自动解析，
 *  否则字符串直写 option（如 grid）会让 ECharts setOption 失败（整图不渲染） */
export function defaultsFromSchema(schema: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!Array.isArray(schema)) return out;
  for (const f of schema) {
    if (isPlainObject(f) && typeof f.field === 'string' && f.default !== undefined) {
      let value: unknown = f.default;
      if (f.control === 'json' && typeof value === 'string' && value.trim()) {
        const t = value.trim();
        if (t.startsWith('{') || t.startsWith('[')) {
          try {
            value = JSON.parse(t);
          } catch {
            /* 非法 JSON：保留字符串（渲染端忽略/降级） */
          }
        }
      }
      out[f.field] = value;
    }
  }
  return out;
}

// ------------------------------------------------------------
// 数据注入
// ------------------------------------------------------------

function applyCategoryLabels(option: Record<string, unknown>, labels: string[]): void {
  const xAxis = option.xAxis as Record<string, unknown> | undefined;
  if (xAxis && !Array.isArray(xAxis) && xAxis.type === 'category') xAxis.data = labels;
}

/** 运行时数据注入（dtwin-web 历史语义 + P2 DSL：x=类目字段 / y=数值字段 / series=多系列字段）
 *  2026-10-17：**无数据行时不覆盖模板**（保留官方示例内置演示数据 → 大屏默认观感与预览一致） */
export function injectRowsData(
  option: Record<string, unknown>,
  dataMapping: unknown,
  rows: Array<Record<string, unknown>>,
): Record<string, unknown> {
  const parsed = parseDataMapping(dataMapping);
  const seriesArr = Array.isArray(option.series) ? (option.series as Array<Record<string, unknown>>) : [];
  if (seriesArr.length === 0) return option;
  if (rows.length === 0) return option;

  if (parsed.kind === 'dsl') {
    const { x, y, series } = parsed.dsl || {};
    if (series && series.length > 0) {
      series.forEach((k, i) => {
        if (seriesArr[i]) seriesArr[i].data = rows.map((r) => Number(r?.[k]) || 0);
      });
      if (x) applyCategoryLabels(option, rows.map((r) => String(r?.[x] ?? '')));
      return option;
    }
    const labels = x
      ? rows.map((r) => String(r?.[x] ?? ''))
      : rows.map((r) => (r?.label ?? r?.name ?? '') as string);
    const values = y ? rows.map((r) => Number(r?.[y]) || 0) : rows.map((r) => Number(r?.value ?? 0));
    applyCategoryLabels(option, labels);
    seriesArr[0].data = values;
    return option;
  }

  if (parsed.keys.length <= 1) {
    const labels = rows.map((r) => (r?.label ?? r?.name ?? '') as string);
    const values = rows.map((r) => Number(r?.value ?? 0));
    applyCategoryLabels(option, labels);
    seriesArr[0].data = values;
  } else {
    parsed.keys.forEach((k, i) => {
      if (seriesArr[i]) {
        seriesArr[i].data = rows.map((r) => Number(r?.[k]) || 0);
      }
    });
  }
  return option;
}

/** 预览数据注入（服务端 SSR/编辑器实时预览历史语义：合成演示数据，逐字保留原行为；P2 支持 dsl series 数） */
export function injectPreviewData(
  option: Record<string, unknown>,
  dataMapping: unknown,
  rowCount = DEFAULT_PREVIEW_ROWS.length,
): Record<string, unknown> {
  const parsed = parseDataMapping(dataMapping);
  const seriesArr = Array.isArray(option.series) ? (option.series as Array<Record<string, unknown>>) : [];
  if (seriesArr.length === 0) return option;

  const dslSeries = parsed.kind === 'dsl' ? parsed.dsl?.series || [] : [];
  const multiKeys = parsed.kind === 'dsl' ? dslSeries : parsed.keys;
  if (multiKeys.length > 1) {
    multiKeys.forEach((_k, i) => {
      if (seriesArr[i]) {
        seriesArr[i].data = Array.from(
          { length: rowCount },
          (_, ri) => PREVIEW_MULTI_BASE[ri % PREVIEW_MULTI_BASE.length] + ((i * 17 + ri * 29) % 50),
        );
      }
    });
    return option;
  }

  const labels = DEFAULT_PREVIEW_ROWS.map((r) => r.label).slice(0, rowCount);
  const values = DEFAULT_PREVIEW_ROWS.map((r) => r.value).slice(0, rowCount);
  applyCategoryLabels(option, labels);
  seriesArr[0].data = values;
  return option;
}

// ------------------------------------------------------------
// 渲染管线（权威）
// ------------------------------------------------------------

/** 公共前置：克隆 → fieldPaths → 高级选项合并（数据注入之前） */
function prepareOption(tpl: DeclarativeTemplate | null | undefined, config?: Record<string, unknown> | null): Record<string, unknown> | null {
  let option: Record<string, unknown>;
  try {
    option = cloneJson(((tpl?.option as Record<string, unknown>) || {}) as Record<string, unknown>);
  } catch {
    return null;
  }
  try {
    applyFieldPaths(option, tpl?.fieldPaths, config);
  } catch {
    /* 单个路径异常不阻断 */
  }
  const advanced = parseOptionAdvanced(config?.optionAdvanced);
  if (advanced) {
    try {
      mergeOptionInto(option, advanced);
    } catch {
      /* 合并异常不阻断 */
    }
  }
  return option;
}

/**
 * 运行时渲染（dtwin-web chartOptionFactory 调用）：
 * clone → fieldPaths → optionAdvanced → 数据注入（真实数据行）。
 */
export function buildDeclarativeOption(
  tpl: DeclarativeTemplate | null | undefined,
  options: DeclarativeBuildOptions = {},
): Record<string, unknown> | null {
  const option = prepareOption(tpl, options.config);
  if (!option) return null;
  return injectRowsData(option, tpl?.dataMapping, options.rows || []);
}

/**
 * 预览渲染（服务端 SSR 缩略图 / 编辑器实时预览）：
 * clone → fieldPaths → optionAdvanced → 合成演示数据注入。
 */
export function buildPreviewOption(
  tpl: DeclarativeTemplate | null | undefined,
  config?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const option = prepareOption(tpl, config);
  if (!option) return null;
  return injectPreviewData(option, tpl?.dataMapping);
}

// ------------------------------------------------------------
// 契约校验（服务端权限 + 编辑器预检共用，消息逐字一致）
// ------------------------------------------------------------

const FIELD_NAME_RE = /^[a-zA-Z][\w-]*$/;
const FIELD_PATH_VALUE_RE = /^[a-zA-Z0-9_*-]+(\.[a-zA-Z0-9_*-]+)*$/;
export const FIELD_PATHS_MAX_ENTRIES = 50;

/** fieldPaths 校验（新增契约；返回错误消息数组，空数组=通过） */
export function validateFieldPaths(fieldPaths: unknown): string[] {
  const errors: string[] = [];
  if (fieldPaths === undefined || fieldPaths === null) return errors;
  if (!isPlainObject(fieldPaths)) {
    errors.push('fieldPaths 须为对象（field → option 路径）');
    return errors;
  }
  const entries = Object.entries(fieldPaths as Record<string, unknown>);
  if (entries.length > FIELD_PATHS_MAX_ENTRIES) {
    errors.push(`fieldPaths 条目超限（≤${FIELD_PATHS_MAX_ENTRIES} 条）`);
    return errors;
  }
  for (const [key, rawPath] of entries) {
    if (!FIELD_NAME_RE.test(key)) {
      errors.push(`fieldPaths 字段名不合法：${key}`);
    }
    const path = String(rawPath ?? '').trim();
    if (!path || !FIELD_PATH_VALUE_RE.test(path)) {
      errors.push(`fieldPaths 路径非法（仅字母数字下划线中划线点号与 *）：${path}`);
      continue;
    }
    if (path.split('.').some((seg) => seg === 'data')) {
      errors.push(`fieldPaths 路径不得指向 data：${path}`);
    }
  }
  return errors;
}

/**
 * 声明式契约校验（schema + optionTemplate）：
 * 消息与 dtwin-server plugin-component.service.upload 历史逐字一致（服务端升级为调用本函数）。
 */
export function validateDeclarativeContract(input: {
  schema?: unknown;
  optionTemplate?: unknown;
}): string[] {
  const errors: string[] = [];
  const schema = Array.isArray(input?.schema) ? input.schema : [];
  for (const f of schema) {
    const r = (f || {}) as Record<string, unknown>;
    if (!r.field || !r.label || !r.group || !r.control) {
      errors.push(`schema 字段缺少 field/label/group/control：${JSON.stringify(r).slice(0, 120)}`);
      continue;
    }
    if (String(r.group) === 'base') {
      errors.push(`schema 字段 ${String(r.field)} 不允许使用 group='base'（通用属性由系统统一提供，与其他组件保持一致）`);
      continue;
    }
    if (!FIELD_NAME_RE.test(String(r.field))) {
      errors.push(`schema 字段名不合法（须字母开头，仅字母数字下划线中划线）：${String(r.field)}`);
    }
  }
  const tpl = input?.optionTemplate as DeclarativeTemplate | undefined | null;
  if (!tpl || typeof tpl !== 'object' || !tpl.option || typeof tpl.option !== 'object' || Array.isArray(tpl.option)) {
    errors.push('声明式组件必须提供 optionTemplate.option（对象）');
  } else {
    const option = tpl.option as Record<string, unknown>;
    if (option.series !== undefined && !Array.isArray(option.series)) {
      errors.push('optionTemplate.option.series 必须为数组');
    }
    // P2：dataMapping 支持 label/value | 逗号分隔字段名 | dsl:x=..,y=..[,series=a|b]
    const mappingError = validateDataMappingValue(tpl.dataMapping);
    if (mappingError) errors.push(mappingError);
  }
  errors.push(...validateFieldPaths(tpl && typeof tpl === 'object' ? (tpl as DeclarativeTemplate).fieldPaths : undefined));
  return errors;
}
